import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isPercentageComplete,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  useGetDairyFeedTypes,
  useGetFootprintingDairyFeedInformationByPrimaryDataId,
  useMutateFootprintingDairyFeedInformation,
  useTranslation,
} from "utils";

import { DAIRY_FEED_ENERGY_UNIT, DairyFeedType, ENUM_DAIRY_FEED_ENERGY_UNIT, UploadableFeedInformation } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputImageCard,
  InputNumber,
  InputNumberWithUnitSelector,
  Option,
  PageSpin,
  Select,
} from "components";

interface DairyFeedTypesFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedTypePlaceholder: UploadableFeedInformation = {
  id: undefined,
  feedTypeId: "",
  totalAmountFedKg: null,
  maizeSilagePct: null,
  grassSilagePct: null,
  energyValue: null,
  energyUnit: null,
  dryMatterPct: null,
  cowsPct: null,
  calvesPct: null,
  emissionFactorKgCo2Eq: null,
};

const getEnergyValuesFromFeedType = (feedType: DairyFeedType | undefined) => {
  if (!feedType) {
    return { energyValue: null, energyUnit: null };
  }

  if (feedType.energyMjMeKgDm != null) {
    return {
      energyValue: feedType.energyMjMeKgDm,
      energyUnit: DAIRY_FEED_ENERGY_UNIT.MJME,
    };
  }

  if (feedType.energyMjNelKgDm != null) {
    return {
      energyValue: feedType.energyMjNelKgDm,
      energyUnit: DAIRY_FEED_ENERGY_UNIT.MJNEL,
    };
  }

  return { energyValue: null, energyUnit: null };
};

export const FeedInformationForm = ({ primaryDataId, readOnly, farmId, year }: DairyFeedTypesFormProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [feedTypes, setFeedTypes] = useState<UploadableFeedInformation[]>([feedTypePlaceholder]);

  const { data: feedInformationQuery, isLoading } = useGetFootprintingDairyFeedInformationByPrimaryDataId(primaryDataId ?? "");
  const { data: dairyFeedTypesQuery } = useGetDairyFeedTypes();

  const selectGroupName = "footprinting-dairy-feed-crop-select";

  const { mutate: mutateFeedInformation } = useMutateFootprintingDairyFeedInformation(
    farmId,
    year,
    feedTypes.filter((f) => !!f.feedTypeId),
    primaryDataId ?? ""
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFeedInformation();
      }
    },
    [isLoading, mutateFeedInformation, readOnly]
  );

  useEffect(() => {
    if (!feedInformationQuery || !dairyFeedTypesQuery || !formRef.current) {
      return;
    }

    const newFeedTypes = feedInformationQuery.map((f) => ({
      id: f.id,
      feedTypeId: f.feedTypeId ?? "",
      totalAmountFedKg: f.totalAmountFedKg ?? null,
      maizeSilagePct: f.maizeSilagePct ?? null,
      grassSilagePct: f.grassSilagePct ?? null,
      energyValue: f.energyValue ?? null,
      energyUnit: f.energyUnit ?? null,
      dryMatterPct: f.dryMatterPct ?? null,
      cowsPct: f.cowsPct ?? null,
      calvesPct: f.calvesPct ?? null,
      emissionFactorKgCo2Eq: f.emissionFactorKgCo2Eq ?? null,
    }));

    setFeedTypes(newFeedTypes);

    const fieldsValue: Record<string, unknown> = {};

    newFeedTypes.forEach((crop, index) => {
      const selectGroupWithIndex = `${selectGroupName}-${index}`;
      const selectedFeedType = dairyFeedTypesQuery.find((ft) => ft.id === crop.feedTypeId);

      fieldsValue[selectGroupWithIndex] = selectedFeedType?.translationSlug || "";
      fieldsValue[`totalAmountFed-${index}`] = crop.totalAmountFedKg;
      fieldsValue[`maizeSilagePct-${index}`] = formatFractionInput(crop.maizeSilagePct ?? null);
      fieldsValue[`grassSilagePct-${index}`] = formatFractionInput(crop.grassSilagePct ?? null);
      fieldsValue[`energyValue-${index}`] = crop.energyValue;
      fieldsValue[`energyValue-${index}-unit`] = crop.energyUnit;
      fieldsValue[`dryMatterPct-${index}`] = formatFractionInput(crop.dryMatterPct ?? null);
      fieldsValue[`cowsPct-${index}`] = formatFractionInput(crop.cowsPct ?? null);
      fieldsValue[`calvesPct-${index}`] = formatFractionInput(crop.calvesPct ?? null);
      fieldsValue[`emissionFactorKgCo2Eq-${index}`] = crop.emissionFactorKgCo2Eq;
    });

    formRef.current.setFieldsValue(fieldsValue);
    formRef.current.validateFields();
  }, [feedInformationQuery, dairyFeedTypesQuery, selectGroupName]);

  const validateTotalSilagePercentage = (v: number, i: number, fieldType: "maize" | "grass") => {
    const currentValueAsDecimal = formatPercentageInput(v) ?? 0;

    const total = feedTypes.reduce((sum, feed, idx) => {
      const maizeValue = idx === i && fieldType === "maize" ? currentValueAsDecimal : feed.maizeSilagePct ?? 0;
      const grassValue = idx === i && fieldType === "grass" ? currentValueAsDecimal : feed.grassSilagePct ?? 0;

      return sum + maizeValue + grassValue;
    }, 0);

    return total <= 1;
  };

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyFeedTypesForm.title")}</h1>
      <Card dataTestId="footprinting-dairy-feed-crops-form" className="margin-bottom-100 padding-100">
        <Flex vertical>
          <Flex vertical className="padding-100" gap={8}>
            <h3>{t("dairyFeedTypesForm.subTitle")}</h3>
            <p className="margin-0 grey-600-text paragraph-m">{t("dairyFeedTypesForm.description")}</p>
          </Flex>

          <Form ref={formRef}>
            {feedTypes.map((crop, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;
              const updateItem = (item: UploadableFeedInformation) => {
                const next = [...feedTypes];
                next[index] = { ...item };
                setFeedTypes(next);
              };

              const selectedFeedType = dairyFeedTypesQuery?.find((ft) => ft.id === crop.feedTypeId);

              const isCornSilage = selectedFeedType?.translationSlug === "corn_silage";
              const isGrassSilage = selectedFeedType?.translationSlug === "grass_silage";
              const showPercentageInputs = isCornSilage || isGrassSilage;

              return (
                <Card key={index} className="margin-bottom-100 feed-cards">
                  <Form.Item className="padding-100">
                    <Flex className="margin-bottom-100" justify="space-between" align="center" vertical={false}>
                      <h5 style={{ flex: 1 }}>
                        {selectedFeedType?.translationSlug
                          ? t("dairyFeedTypesForm.feedTypeCount", {
                              count: index + 1,
                              cropName: t(`feedTypes.${selectedFeedType.translationSlug}`, { ns: "dairy" }),
                            })
                          : t("dairyFeedTypesForm.feedTypeCountNoName", { count: index + 1 })}
                      </h5>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-feed-crop-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => setFeedTypes([...feedTypes].filter((_, i) => i !== index))}
                      />
                    </Flex>

                    <Form.Item className="margin-bottom-100">
                      {/* @ts-expect-error need to convert to tsx */}
                      <Select
                        showSearch={true}
                        disabled={readOnly}
                        name={selectGroupWithIndex}
                        dataTestId={selectGroupWithIndex}
                        label={t("dairyFeedTypesForm.question.feedType")}
                        rules={[{ required: true, message: false }]}
                        initialValue={selectedFeedType?.translationSlug || ""}
                        value={selectedFeedType?.translationSlug || ""}
                        onChange={(v) => {
                          const feedType = dairyFeedTypesQuery?.find((f) => f.translationSlug === v);
                          const { energyValue, energyUnit } = getEnergyValuesFromFeedType(feedType);

                          updateItem({
                            ...crop,
                            feedTypeId: feedType?.id ?? "",
                            energyValue,
                            energyUnit,
                            dryMatterPct: feedType?.dryMatterPerFeedTypePct ?? null,
                          });

                          formRef.current?.setFieldsValue({
                            [`energyValue-${index}`]: energyValue,
                            [`energyValue-${index}-unit`]: energyUnit,
                            [`dryMatterPct-${index}`]: formatFractionInput(feedType?.dryMatterPerFeedTypePct ?? null),
                          });
                        }}
                        dropdownStyle={{ zIndex: 10010 }}
                      >
                        {(dairyFeedTypesQuery ?? []).map((feedType) => {
                          // Check if this feed type ID is already used in another item
                          const isAlreadyUsed = feedTypes.some((f) => f.feedTypeId === feedType.id && f.feedTypeId !== crop.feedTypeId);

                          return (
                            <Option
                              disabled={readOnly || isAlreadyUsed}
                              key={`${selectGroupWithIndex}-${feedType.id}`}
                              data-testid={`${selectGroupWithIndex}-${feedType.id}`}
                              value={feedType.translationSlug}
                            >
                              {t(`feedTypes.${feedType.translationSlug}`, { ns: "dairy" })}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>

                    {isCornSilage && (
                      <InputNumber
                        className="margin-bottom-100"
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-maize-silage-pct-${index}`}
                        name={`maizeSilagePct-${index}`}
                        label={t("dairyFeedTypesForm.question.maizeSilagePct")}
                        suffix="%"
                        value={formatFractionInput(crop.maizeSilagePct ?? null)}
                        initialValue={formatFractionInput(crop.maizeSilagePct ?? null)}
                        onChange={(v) => {
                          updateItem({ ...crop, maizeSilagePct: formatPercentageInput(v) });
                          setTimeout(() => {
                            feedTypes.forEach((_, idx) => {
                              formRef.current?.validateFields([`maizeSilagePct-${idx}`, `grassSilagePct-${idx}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        validateFirst
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validPercentage")));
                              }
                              if (!validateTotalSilagePercentage(v, index, "maize")) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.maizeAndGrassSilagePercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    )}

                    {isGrassSilage && (
                      <InputNumber
                        className="margin-bottom-100"
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-grass-silage-pct-${index}`}
                        name={`grassSilagePct-${index}`}
                        label={t("dairyFeedTypesForm.question.grassSilagePct")}
                        suffix="%"
                        value={formatFractionInput(crop.grassSilagePct ?? null)}
                        initialValue={formatFractionInput(crop.grassSilagePct ?? null)}
                        onChange={(v) => {
                          updateItem({ ...crop, grassSilagePct: formatPercentageInput(v) });
                          setTimeout(() => {
                            feedTypes.forEach((_, idx) => {
                              formRef.current?.validateFields([`maizeSilagePct-${idx}`, `grassSilagePct-${idx}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        validateFirst
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validPercentage")));
                              }
                              if (!validateTotalSilagePercentage(v, index, "grass")) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.maizeAndGrassSilagePercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    )}

                    {!showPercentageInputs && (
                      <InputNumber
                        className="margin-bottom-100"
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-amount-input-${index}`}
                        name={`totalAmountFed-${index}`}
                        label={t("dairyFeedTypesForm.question.totalAmountFed")}
                        suffix={t("dairyFeedTypesForm.question.totalAmountFedUnit")}
                        value={crop.totalAmountFedKg}
                        initialValue={crop.totalAmountFedKg}
                        onChange={(v) => updateItem({ ...crop, totalAmountFedKg: v })}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validAmount")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    )}

                    <div className="margin-top-200">
                      <h6 className="margin-bottom-100">{t("dairyFeedTypesForm.basicInformation")}</h6>

                      <Form.Item className="margin-bottom-100">
                        <InputNumberWithUnitSelector
                          disabled={readOnly}
                          dataTestId={`footprinting-dairy-feed-energy-input-${index}`}
                          name={`energyValue-${index}`}
                          label={t("dairyFeedTypesForm.question.energyValue")}
                          unitLabel={t("unit", { ns: "footprinting" })}
                          units={Object.values(DAIRY_FEED_ENERGY_UNIT)}
                          value={crop.energyValue}
                          initialValue={crop.energyValue}
                          unitValue={crop.energyUnit}
                          onChange={(v, unit) => updateItem({ ...crop, energyValue: v, energyUnit: unit as ENUM_DAIRY_FEED_ENERGY_UNIT })}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                  return Promise.reject(new Error(t("dairyFeedTypesForm.error.validNumber")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item className="margin-bottom-100">
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-dairy-feed-drymatter-input-${index}`}
                          name={`dryMatterPct-${index}`}
                          label={t("dairyFeedTypesForm.question.dryMatterPct")}
                          suffix="%"
                          value={formatFractionInput(crop.dryMatterPct ?? null)}
                          initialValue={formatFractionInput(crop.dryMatterPct ?? null)}
                          onChange={(v) => updateItem({ ...crop, dryMatterPct: formatPercentageInput(v) })}
                          precision={2}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("dairyFeedTypesForm.error.validPercentage")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>
                    </div>

                    <div className="margin-top-200">
                      <h6>{t("dairyFeedTypesForm.feedDistribution")}</h6>
                      <p className="margin-0 grey-600-text paragraph-m">{t("dairyFeedTypesForm.feedDistributionDescription")}</p>

                      <Form.Item>
                        <InputImageCard
                          disabled={readOnly}
                          title={t("dairyFeedTypesForm.question.cowsPct")}
                          tooltip={t("dairyFeedTypesForm.tooltip.cowsPct")}
                          suffix="%"
                          name={`cowsPct-${index}`}
                          value={formatFractionInput(crop.cowsPct ?? null)}
                          initialValue={formatFractionInput(crop.cowsPct ?? null)}
                          onChange={(v) => {
                            updateItem({ ...crop, cowsPct: formatPercentageInput(v) });
                            setTimeout(() => {
                              feedTypes.forEach((_, idx) => {
                                formRef.current?.validateFields([`cowsPct-${idx}`, `calvesPct-${idx}`]);
                              });
                            }, 0);
                          }}
                          precision={2}
                          image={{
                            imageAlt: "Cow image",
                            imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/mixed_breed_dairy.png",
                          }}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject();
                                }

                                if (v / 100 + (crop.calvesPct ?? 0) > 1) {
                                  return Promise.reject();
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item className="margin-bottom-100">
                        <InputImageCard
                          disabled={readOnly}
                          title={t("dairyFeedTypesForm.question.calvesPct")}
                          tooltip={t("dairyFeedTypesForm.tooltip.calvesPct")}
                          suffix="%"
                          name={`calvesPct-${index}`}
                          value={formatFractionInput(crop.calvesPct ?? null)}
                          initialValue={formatFractionInput(crop.calvesPct ?? null)}
                          onChange={(v) => {
                            updateItem({ ...crop, calvesPct: formatPercentageInput(v) });
                            setTimeout(() => {
                              feedTypes.forEach((_, idx) => {
                                formRef.current?.validateFields([`cowsPct-${idx}`, `calvesPct-${idx}`]);
                              });
                            }, 0);
                          }}
                          precision={2}
                          image={{
                            imageSize: 48,
                            imageAlt: "Calves image",
                            imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/calve.png",
                          }}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject();
                                }

                                if (v / 100 + (crop.cowsPct ?? 0) > 1) {
                                  return Promise.reject();
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>

                      {crop.cowsPct === null || crop.calvesPct === null || isPercentageComplete((crop.cowsPct ?? 0) + (crop.calvesPct ?? 0)) ? null : (
                        <AlertBanner
                          type="warning"
                          iconName="tabler/alert-triangle"
                          iconAlign="start"
                          iconSize={24}
                          headingSize="h6"
                          description={t("dairyFeedTypesForm.error.invalidDistributionDescription")}
                          title={
                            (crop.cowsPct ?? 0) + (crop.calvesPct ?? 0) > 1
                              ? t("dairyFeedTypesForm.error.invalidDistributionTitleTooHigh")
                              : t("dairyFeedTypesForm.error.invalidDistributionTitleTooLow")
                          }
                        />
                      )}
                    </div>
                  </Form.Item>
                </Card>
              );
            })}

            <Button
              type="text"
              dataTestId="add-feed-crop-button"
              disabled={readOnly}
              onClick={() => {
                setFeedTypes([...feedTypes, { ...feedTypePlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyFeedTypesForm.addFeedType")}
            </Button>
          </Form>
        </Flex>
      </Card>
    </div>
  );
};
